












































import { computed, defineComponent, onMounted, PropType, ref, toRefs, watch } from '@vue/composition-api';
import { createLogger, queryToJson } from '@tt4b/ttam-utils';
import { AdgroupFormData, AdType, FullNormalFormData, ProductItem, PreviewMaterial } from '@tt4b/ttam-enums';
import CreativePreviewer from './../common/creative-previewer/index';

import NoPreviewIcon from '@tt4b/ttam-assets/src/svgs/no-preview.svg';
import PreviewMiddle from './preview-middle/index.vue';
import {
  useGetCatalogTitle,
  useGetPreviewProducts,
  useProductRenderImageMeta,
  useProductRenderMeta,
  useSelectVideoPackage,
  useGetVideoPackageByFormData,
  useGetPreviewProductsByFormData,
} from './hook';
import {
  StateEnum,
  useCreativeActionTypeIs,
  useStore,
  watchBroadcastChannel,
  BroadcastChannelEvents,
} from '@tt4b/ttam-hooks';
import { CreateMode } from '@ad/tt_ads_common_enums';

const logger = createLogger('TtamCatalogsPreviewCompose');

const name = 'ttam-catalogs-preview-compose';
export default defineComponent({
  name,
  components: {
    CreativePreviewer,
    PreviewMiddle,
    NoPreviewIcon,
  },
  props: {
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
    },
    creativeFormData: {
      type: Object as PropType<FullNormalFormData>,
      required: true,
      default: () => ({} as FullNormalFormData),
    },
    additionalInfo: {
      type: Object,
      default: () => ({}),
    },
    isCatalogImage: {
      type: Boolean,
      default: false,
    },
    templateId: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    avatarIcon: {
      type: String,
      default: '',
    },
    actionLabel: {
      type: String,
      default: '',
    },
    createMode: {
      type: String,
      default: CreateMode.CREATE,
    },
    isSmartCreative: {
      type: Boolean,
      default: false,
    },
    videoMaterial: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const { aadvid } = queryToJson();
    const {
      adgroupData,
      creativeFormData,
      displayName,
      avatarIcon,
      actionLabel,
      additionalInfo,
      isCatalogImage,
      createMode,
      isSmartCreative,
      videoMaterial,
    } = toRefs(props);

    const catalogId = computed(() => adgroupData.value.productPlatformId);
    const bcId = computed(() => adgroupData.value.catalogAuthorizedBc);
    const pSetId = computed(() => creativeFormData.value.productSetId);
    const productIds = computed(() => creativeFormData.value.dpaProducts);
    const templateId = computed(() => creativeFormData.value.videoTplName);
    const { isCreate } = useCreativeActionTypeIs({
      createMode,
      adgroupFormData: adgroupData,
    });

    const { state } = useStore();
    const catalogWordList = computed(() => state[StateEnum.catalogLexiconList].data.value);

    const { previewProductList, remote, getPreviewProducts, isLoading } = useGetPreviewProducts(
      catalogId,
      bcId,
      pSetId,
      productIds,
    );

    const queryKey = ref('');
    const filterProductList = computed(() =>
      previewProductList.value.filter(product => product.OuterID?.indexOf?.(queryKey.value) > -1),
    );

    const previewTabs = ref('video');
    const handleSwitch = (val: string) => {
      previewTabs.value = val;
    };

    /**
     * 此时preview的是否是video
     */
    const isPreviewVideo = computed(() => isSmartCreative.value && ['video'].includes(previewTabs.value));

    const { videoPackages, videoPackagesLoaded } = useGetVideoPackageByFormData({
      adgroupData,
    });
    const curVideoPackage = computed(() => {
      if (templateId.value) {
        return videoPackages.value?.find?.(pkg => pkg.packageId === templateId.value);
      }
      return null;
    });

    const {
      getProductRenderMeta,
      productRenderMeta,
      isLoadingMeta,
      productRenderError,
      setNoProductError,
      productPreviewIndicate,
      currentProduct,
    } = useProductRenderMeta(catalogId, bcId, curVideoPackage, videoPackages);

    /** 上面的previewProductList 会被query污染 */
    const { previewProductList: defaultPreviewProductList } = useGetPreviewProductsByFormData({
      adgroupData,
      creativeData: creativeFormData,
    });
    const isShowSelect = computed(() => {
      // 没有video url
      const hasVideoUrl = Boolean(defaultPreviewProductList.value?.[0]?.video?.videoId);
      const hasTemplate = videoPackages.value?.length > 0;
      // 既没有video template，也没有video url
      return hasVideoUrl || hasTemplate;
    });

    const { productRenderImageMeta, getProductRenderImageMeta, productImageRenderError } = useProductRenderImageMeta({
      catalogId,
      bcId,
      actionLabel,
    });

    const { title: productLabel } = useGetCatalogTitle({
      adgroupData,
      creativeData: creativeFormData,
    });

    const isShowProductRenderError = computed(() => {
      if (isPreviewVideo.value) {
        return null;
      }
      return (
        (isCatalogImage.value && productImageRenderError.value) || (!isCatalogImage.value && productRenderError.value)
      );
    });
    const productRenderErrorMessage = computed(() =>
      isCatalogImage.value ? productImageRenderError.value?.message : productRenderError.value?.message,
    );
    const productRenderErrorButton = computed(() =>
      isCatalogImage.value ? productImageRenderError.value?.cta : productRenderError.value?.cta,
    );

    const materialData = computed(() => {
      if (isPreviewVideo.value) {
        return videoMaterial.value;
      }
      return isCatalogImage.value ? productRenderImageMeta.value : productRenderMeta.value;
    });

    const config = computed(() => {
      const configVal = {
        apps: adgroupData.value.inventoryFlow,
        info: {
          name: displayName.value,
          avatar: avatarIcon.value,
        },
        material: {
          type: PreviewMaterial.TIKTOK_SHOP,
          label: productLabel.value,
          actionLabel: actionLabel.value,
          ...materialData.value,
        },
        additionalInfo: additionalInfo.value,
        otherInfo: {
          aadvid,
          isLoading: isLoadingMeta.value,
          pangleIframeData: {
            adType: isCatalogImage.value ? AdType.CATALOGUE_IMAGE : AdType.CATALOGUE_VIDEO,
            creativeFormData: creativeFormData.value,
          },
        },
        // 非常魔幻，CatalogImage必须加这个空对象
        placementControl: {},
      };

      // logger.debug('CatalogsPreview videoConfig=%s', configVal);
      return configVal;
    });

    const onChange = item => {
      getProductRenderImageMeta(item);
      getProductRenderMeta(item);
    };
    const onQuery = (outerId: string) => {
      if (remote.value) {
        getPreviewProducts({ outerId });
      } else {
        queryKey.value = outerId;
      }
    };

    const onUpdatePreviewProduct = async (product: ProductItem) => {
      if (previewProductList.value?.length > 0) {
        getProductRenderImageMeta(product);
        await getProductRenderMeta(product);
      } else {
        setNoProductError();
      }
    };

    const onUpdatePreviewProducts = async () => {
      queryKey.value = '';
      await getPreviewProducts({ templateId: curVideoPackage.value?.packageId });
      await onUpdatePreviewProduct(previewProductList.value[0]);
    };

    const onUpdateAll = async () => {
      // 这里一定要等视频包数据回来才能开始预览，因为实时预览需要视频包数据
      if (videoPackagesLoaded.value) {
        await onUpdatePreviewProducts();
      }
    };

    const onClickErrorCta = () => {
      if (isCatalogImage.value) {
        productImageRenderError.value?.ctaCb?.();
      } else {
        productRenderError?.value?.ctaCb?.();
      }
    };

    onMounted(() => {
      watch(videoPackagesLoaded, onUpdateAll);
      watch(() => pSetId.value + JSON.stringify(productIds.value), onUpdatePreviewProducts);

      watch(productRenderMeta, () => {
        emit('render-info-changed', productRenderMeta.value);
      });
      watch(templateId, () => {
        if (currentProduct.value) {
          onUpdatePreviewProduct(currentProduct.value);
        }
      });

      onUpdateAll();
    });

    const previewerChangeToSingleVideo = () => {
      if (isCreate.value) {
        emit('change-to-video');
      }
    };
    const previewerUseVideoEditor = () => {
      if (isCreate.value) {
        emit('use-video-editor');
      }
    };

    watchBroadcastChannel(BroadcastChannelEvents.CATALOG_VIDEOTPL_CHANGE, onUpdateAll);

    return {
      config,
      remote,
      curVideoPackage,
      isLoading,
      previewProductList,
      filterProductList,
      productRenderError,
      productImageRenderError,
      videoPackages,
      onQuery,
      onChange,
      onClickErrorCta,
      catalogWordList,
      previewerChangeToSingleVideo,
      previewerUseVideoEditor,
      isShowProductRenderError,
      productRenderErrorMessage,
      productRenderErrorButton,
      productPreviewIndicate,
      currentProduct,
      previewTabs,
      handleSwitch,
      isShowSelect,
    };
  },
});
